import type { PropsWithChildren } from 'react';
import {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useState
} from 'react';
import { useEvaluateFormulasDebounced } from '@elfsight/widget-calculation';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import hash from 'hash-sum';
import { useCalculatorConditionalLogic } from './conditional-logic-context';
import { useFieldsValues } from './use-fields-values';

const CalculationsContext = createContext<Record<string, string | undefined>>(
  {}
);

export function CalculationsProvider({ children }: PropsWithChildren) {
  const { displayedFields, displayedCalculations } =
    useCalculatorConditionalLogic();
  const { fields: changedFields } = useFormSelector();

  const [results, setResults] = useState({});
  const deferredResults = useDeferredValue(results);

  const evaluateFormulasDebounced = useEvaluateFormulasDebounced();

  const fieldsWithData = useFieldsValues(displayedFields, { ...changedFields });
  const scopeRef = createHashDependency(
    Object.fromEntries(fieldsWithData.map(({ id, value }) => [id, value]))
  );
  const formulasRef = createHashDependency(
    Object.fromEntries(
      displayedCalculations.map(({ id, formula }) => [id, formula])
    )
  );

  useEffect(() => {
    evaluateFormulasDebounced(
      formulasRef.current,
      scopeRef.current,
      setResults
    );
  }, [formulasRef.hash, scopeRef.hash]);

  return (
    <CalculationsContext.Provider value={deferredResults}>
      {children}
    </CalculationsContext.Provider>
  );
}

function createHashDependency<T>(current: T): {
  current: T;
  hash: string;
} {
  return {
    current,
    hash: hash(current)
  };
}

export const useCalculations = () => {
  return useContext(CalculationsContext);
};
